import { useMemo, useEffect, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { observer } from "mobx-react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import commonStore from "./Store/marco/commonStore";
import { useLocation } from "react-router-dom";
import { EmissionViewContainer } from "./EmissionView";
import { abortAll } from "./services/abortManager";
import { useAutoUpdate } from "./hooks/useAutoUpdate";
import SystemUpdateRecovery from "./Components/Common/SystemUpdateRecovery";

// Lazy Components
import SSOLoginHandler from "./login/components/SSOLoginHandler";
import { LoginContainer2 } from "./login/components/LoginContainer";
import ResetPassword from "./login/components/ResetPassword";
import DashboardContainerV4 from "./Dashboardv4";
import NavBar from "./App/NavBar";
import FooterBar from "./App/FooterBar";
import SideBarv4 from "./App/SideBarv4";
import ImportJobModal from "./import-job/ImportJobModal";

const EmissionFactorManagement = lazy(
  () => import("./EmissionFactorManagement"),
);
const TranslationContainer = lazy(() =>
  import("./Containers/Translation/TranslationContainer").then((mod) => ({
    default: mod.TranslationContainer,
  })),
);
const DataQualityManagementContainer = lazy(
  () =>
    import("./DataQualityManagement/components/DataQualityManagementContainer"),
);

const UncertainlyViewContainer = lazy(() =>
  import("./Uncertainly/components/UncertainlyViewContainer").then((mod) => ({
    default: mod.UncertainlyViewContainer,
  })),
);
const ReportGenerationContainer = lazy(
  () => import("./ReportGeneration/components/ReportGenerationContainer"),
);

const BoundarySettingView = lazy(() => import("./BoundarySettings"));
const Release = lazy(() =>
  import("./Release/components/Release").then((mod) => ({
    default: mod.Release,
  })),
);
const OrganizationalStructureContainer = lazy(
  () =>
    import("./OrganizationalStructure/components/OrganizationalStructureContainer"),
);
const Profile = lazy(() => import("./Profile/Profile"));
const SubscriptionContent = lazy(
  () => import("./SubscriptionContent/components/SubscriptionContent"),
);
const UserActivityLog = lazy(
  () => import("./UserActivityLog/components/UserActivityLogContainer"),
);
const OnlineVerificationView = lazy(() =>
  import("./OnlineVerificationView/components/OnlineVerificationView").then(
    (mod) => ({ default: mod.OnlineVerificationView }),
  ),
);
const AuditorVerificationView = lazy(() =>
  import("./AuditorVerificationView/components/AuditorVerificationView").then(
    (mod) => ({ default: mod.AuditorVerificationView }),
  ),
);
const GuidanceForEmissionActivityContainer = lazy(() =>
  import("./GuidanceForEmissionActivity/components/GuidanceForEmissionActivityContainer").then(
    (mod) => ({ default: mod.GuidanceForEmissionActivityContainer }),
  ),
);
const EmissionActivityOverviewContainer = lazy(() =>
  import("./EmissionActivityOverview/components/EmissionActivityOverviewContainer").then(
    (mod) => ({ default: mod.EmissionActivityOverviewContainer }),
  ),
);

// 主題設定
const lightTheme = createTheme({ palette: { mode: "light" } });

// 私有路由元件
const PrivateRoute = ({ auth: { isAuthenticated }, children }) => {
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const App = observer(() => {
  const authenticated = Boolean(commonStore.token);
  const { roleId } = commonStore;
  const location = useLocation();

  //////// 處理自動更新與ChunkError
  const { isRecovering } = useAutoUpdate();

  // 頁面樣式與主題初始化
  useEffect(() => {
    document.body.style.backgroundImage =
      "linear-gradient(-60deg,#d8e2f3,#d8e2f3)";
    localStorage.setItem("theme", "light");
  }, []);

  useEffect(() => {
    return () => {
      abortAll();
    };
  }, [location.pathname]);

  //////// 定義路由與權限對應
  const protectedRoutes = useMemo(
    () => [
      {
        path: "/dashboard",
        component: <DashboardContainerV4 />,
        permissionRoles: [2, 3, 4, 6, 7],
      },

      {
        path: "/emission-view",
        component: <EmissionViewContainer />,
        permissionRoles: [2, 3, 4, 6, 7],
      },
      {
        path: "/data-quality-management",
        component: <DataQualityManagementContainer />,
        permissionRoles: [2, 3, 4, 6, 7],
      },
      {
        path: "/uncertainty-assessment",
        component: <UncertainlyViewContainer />,
        permissionRoles: [2, 3, 4, 6, 7],
      },
      {
        path: "/report-generation",
        component: <ReportGenerationContainer />,
        permissionRoles: [2, 7],
      },
      {
        path: "/boundary-setting",
        component: <BoundarySettingView />,
        permissionRoles: [2, 7],
      },
      {
        path: "/release-notes",
        component: <Release />,
        permissionRoles: [2, 3, 4, 6, 7],
      },
      {
        path: "/subscription-contents",
        component: <SubscriptionContent />,
        permissionRoles: [2, 3, 4, 7],
      },
      {
        path: "/emmision-factor-classification",
        component: <EmissionFactorManagement />,
        permissionRoles: [2, 3, 4, 6, 7],
      },
      {
        path: "/organizational-structure",
        component: <OrganizationalStructureContainer />,
        permissionRoles: [2, 7],
      },
      {
        path: "/activity-log",
        component: <UserActivityLog />,
        permissionRoles: [2, 3, 4, 6, 7],
      },
      {
        path: "/profile",
        component: <Profile />,
        permissionRoles: [2, 3, 4, 5, 6, 7],
      },
      {
        path: "/online-verification",
        component: <OnlineVerificationView />,
        permissionRoles: [2, 5],
      },
      {
        path: "/auditor-verification",
        component: <AuditorVerificationView />,
        permissionRoles: [5],
      },
      {
        path: "/guidance-for-emission-activity",
        component: <GuidanceForEmissionActivityContainer />,
        permissionRoles: [2, 3, 4, 6, 7],
      },
      {
        path: "/emission-activity-overview",
        component: <EmissionActivityOverviewContainer />,
        permissionRoles: [2, 3, 4, 6, 7],
      },
    ],
    [],
  );

  //////// 過濾當前角色可進入的路由
  const filteredRoutes = useMemo(
    () =>
      protectedRoutes.filter(({ permissionRoles }) =>
        permissionRoles.includes(Number(roleId)),
      ),
    [roleId],
  );

  //////// 設定角色預設進入頁面
  const defaultPath = useMemo(() => {
    const roleDefaultMap = {
      1: "none",
      2: "/dashboard",
      3: "/dashboard",
      4: "/dashboard",
      5: "/auditor-verification",
      6: "/dashboard",
      7: "/dashboard",
    };
    return roleDefaultMap[roleId] || "/login";
  }, [roleId]);

  return (
    <ThemeProvider theme={lightTheme}>
      {isRecovering && <SystemUpdateRecovery />}
      <div
        className="body-scroll"
        style={{ paddingBottom: 20, paddingTop: 75 }}
      >
        <NavBar authenticated={authenticated} />
        {authenticated && <ImportJobModal />}

        <div style={{ display: "flex", gap: 20, height: "100%" }}>
          {filteredRoutes.length > 0 && (
            <SideBarv4 authenticated={authenticated} />
          )}

          <Routes>
            <Route
              path="/login"
              element={
                authenticated ? (
                  <Navigate to={defaultPath} replace />
                ) : (
                  <LoginContainer2 />
                )
              }
            />

            <Route path="/sso-login" element={<SSOLoginHandler />} />

            <Route
              path="/reset-password"
              element={
                authenticated ? (
                  <Navigate to={defaultPath} replace />
                ) : (
                  <ResetPassword />
                )
              }
            />

            {filteredRoutes.map(({ path, component }, idx) => (
              <Route
                key={idx}
                path={path}
                element={
                  <PrivateRoute auth={{ isAuthenticated: authenticated }}>
                    <div
                      style={{
                        marginLeft: "70px",
                        width: "100%",
                        height: "100%",
                        overflow: "auto",
                      }}
                    >
                      {component}
                    </div>
                  </PrivateRoute>
                }
              />
            ))}

            <Route
              path="/"
              element={
                <Navigate to={authenticated ? defaultPath : "/login"} replace />
              }
            />
            <Route
              path="*"
              element={
                <Navigate to={authenticated ? defaultPath : "/login"} replace />
              }
            />
          </Routes>
        </div>

        <ToastContainer autoClose={1500} limit={1} />
        <FooterBar authenticated={authenticated} />
      </div>
    </ThemeProvider>
  );
});

export default App;
